<template>
    <!-- Tela de fundo com opacidade -->
  <div
    class="signature-overlay fixed inset-0 flex items-center justify-center w-[100vw] h-[100vh] z-[101]"
    @click.self="closeModal"
  >
    <div class="signature-panel flex flex-col gap-4 p-4 w-full max-w-4xl mx-auto">
      <!-- Header with Close button -->
      <div class="signature-header w-full flex justify-between items-center gap-2">
        <h3 class="signature-title text-xl font-bold">Assinatura</h3>
        <button
          @click="closeModal"
          class="signature-close transition-colors"
          title="Fechar"
        >
          <i class="fa-regular fa-circle-xmark fa-xl"></i>
        </button>
      </div>

      <!-- Signature Area -->
      <div class="signature-container relative bg-[#f8f8f8] border border-[#ccc] rounded-md w-full h-[30vh] sm:h-[40vh] md:h-[50vh]">
        <canvas
          ref="canvas"
          class="w-full h-full"
        ></canvas>
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
          <span class="text-gray-600">Carregando...</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-wrap gap-3 justify-center">
        <!-- Desfazer Button -->
        <button
          @click="undo"
          class="signature-btn signature-btn--undo"
        >
          <i class="fa-solid fa-rotate-left"></i>
          <span>Desfazer</span>
        </button>

        <!-- Limpar Button -->
        <button
          @click="clear"
          class="signature-btn signature-btn--clear"
        >
          <i class="fa-solid fa-trash"></i>
          <span>Limpar</span>
        </button>

        <!-- Salvar Button -->
        <button
          @click="save"
          class="signature-btn signature-btn--save"
        >
          <i class="fa-regular fa-floppy-disk"></i>
          <span>Salvar</span>
        </button>
      </div>

      <!-- Color Picker -->
      <div class="signature-colors flex flex-col gap-2 mt-2">
        <h2 class="text-lg font-semibold">Escolha a cor</h2>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="(color, index) in colors"
            :key="index"
            :style="{ backgroundColor: color }"
            class="signature-swatch w-8 h-8 rounded-full border-2 flex items-center justify-center cursor-pointer"
            @click="setPenColor(color)"
          >
            <span v-if="penColor === color" class="text-white font-bold">✓</span>
          </button>
        </div>
      </div>

      <!-- Thickness Control -->
      <div class="signature-thickness flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <h2 class="text-lg font-semibold">Espessura</h2>
          <input
            type="range"
            v-model="penWidth"
            min="1"
            max="10"
            class="signature-range cursor-pointer flex-1"
            @input="updatePenWidth"
          />
          <span class="w-8 text-center">{{ penWidth }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import SignaturePad from 'signature_pad'

const emit = defineEmits(['save', 'close'])
const props = defineProps({
  initialSignature: {
    type: String,
    default: null
  }
})

// Refs
const canvas = ref(null)
const signaturePad = ref(null)
const loading = ref(true)

// Settings
const colors = ['#000000', '#3385ff', '#55ff33', '#ff5533']
const penColor = ref('#000000')
const penWidth = ref(4)

// Initialize
onMounted(async () => {
  await nextTick()
  try {
    initSignaturePad()
    if (props.initialSignature) {
      await loadInitialSignature(props.initialSignature)
    }
  } catch (error) {
    console.error('Initialization error:', error)
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  if (signaturePad.value) {
    signaturePad.value.off()
  }
  window.removeEventListener('resize', handleResize)
})

function initSignaturePad() {
  if (!canvas.value) throw new Error('Canvas element not found')
  
  signaturePad.value = new SignaturePad(canvas.value, {
    backgroundColor: 'rgb(255, 255, 255)',
    penColor: penColor.value,
    minWidth: 0.5,
    maxWidth: penWidth.value,
    throttle: 16,
    velocityFilterWeight: 0.7
  })

  window.addEventListener('resize', handleResize)
  handleResize()
}

function handleResize() {
  if (!canvas.value || !signaturePad.value) return
  
  const ratio = Math.max(window.devicePixelRatio || 1, 1)
  canvas.value.width = canvas.value.offsetWidth * ratio
  canvas.value.height = canvas.value.offsetHeight * ratio
  canvas.value.getContext('2d').scale(ratio, ratio)
  signaturePad.value.clear()
}

async function loadInitialSignature(dataUrl) {
  if (!signaturePad.value) return
  
  try {
    await signaturePad.value.fromDataURL(dataUrl)
  } catch (error) {
    console.error('Error loading signature:', error)
    throw error
  }
}

// Button Actions
function setPenColor(color) {
  penColor.value = color
  if (signaturePad.value) {
    signaturePad.value.penColor = color
  }
}

function updatePenWidth() {
  if (signaturePad.value) {
    signaturePad.value.minWidth = penWidth.value * 0.7
    signaturePad.value.maxWidth = penWidth.value
  }
}

function undo() {
  if (signaturePad.value) {
    const data = signaturePad.value.toData()
    if (data && data.length > 0) {
      data.pop()
      signaturePad.value.fromData(data)
    }
  }
}

function clear() {
  if (signaturePad.value) {
    signaturePad.value.clear()
  }
}

function save() {
  if (!signaturePad.value || signaturePad.value.isEmpty()) {
    alert('Por favor, forneça sua assinatura antes de salvar')
    return
  }

  try {
    const dataUrl = signaturePad.value.toDataURL('image/png')
    emit('save', dataUrl)
    closeModal()
  } catch (error) {
    console.error('Error saving signature:', error)
    alert('Erro ao salvar assinatura')
  }
}

function closeModal() {
  emit('close')
}
</script>

<style scoped>
/*
  Tokens locais. O valor por omissão é o visual original (usado fora do
  .glass-app, ex. app do técnico, que não tem o tema glass); dentro do
  .glass-app passam a vir das variáveis do tema (escuro / claro).
*/
.signature-overlay {
  --sig-panel: #5D5D98;
  --sig-text: #ffffff;
  --sig-muted: #ffffff;
  --sig-close-hover: #ff9b05;
  --sig-border: transparent;
  --sig-shadow: none;
  --sig-swatch-border: #333333;
  --sig-accent: auto;
  background: var(--bg-pop-up, #0000005a);
}

.glass-app .signature-overlay {
  --sig-panel: var(--fundoApp);
  --sig-text: var(--text);
  --sig-muted: var(--glass-muted);
  --sig-close-hover: var(--text);
  --sig-border: var(--glass-border);
  --sig-shadow: var(--glass-shadow);
  --sig-swatch-border: var(--glass-muted);
  --sig-accent: var(--button);
}

.signature-panel {
  background: var(--sig-panel);
  color: var(--sig-text);
  border: 1px solid var(--sig-border);
  border-radius: 10px;
  box-shadow: var(--sig-shadow);
}

.glass-app .signature-panel {
  border-radius: 0.5rem;
}

.glass-app .signature-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--glass-border);
}

.signature-title {
  color: var(--titulo, #ff9b05);
}

.signature-close {
  color: var(--sig-muted);
}

.signature-close:hover {
  color: var(--sig-close-hover);
}

.signature-swatch {
  border-color: var(--sig-swatch-border);
}

.signature-range {
  accent-color: var(--sig-accent);
}

/* Botões de acção */
.signature-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  color: #ffffff;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  transition: background-color 0.15s, border-color 0.15s, filter 0.15s;
}

.signature-btn--undo { background: #3b82f6; }
.signature-btn--undo:hover { background: #2563eb; }

.signature-btn--clear { background: #ef4444; }
.signature-btn--clear:hover { background: #dc2626; }

.signature-btn--save { background: #ff9b05; }
.signature-btn--save:hover { background: #e68a00; }

/* Tema glass (escuro e claro): mesmo padrão de cardButton / botões de acção */
.glass-app .signature-btn--undo {
  background: var(--primary);
  border-color: var(--button);
  color: var(--text);
}

.glass-app .signature-btn--clear {
  background: color-mix(in srgb, var(--glass-red) 14%, transparent);
  border-color: color-mix(in srgb, var(--glass-red) 40%, transparent);
  color: var(--glass-red);
}

.glass-app .signature-btn--save {
  background: var(--icon);
  border-color: var(--icon);
  color: var(--text-light);
}

.glass-app .signature-btn--undo:hover,
.glass-app .signature-btn--clear:hover,
.glass-app .signature-btn--save:hover {
  filter: brightness(1.12);
}

.glass-app .signature-btn--undo:hover { background: var(--primary); }
.glass-app .signature-btn--clear:hover { background: color-mix(in srgb, var(--glass-red) 22%, transparent); }
.glass-app .signature-btn--save:hover { background: var(--icon); }

.signature-container {
  position: relative;
  touch-action: none;
}

canvas {
  display: block;
  background-color: white;
  touch-action: none;
}

button:active {
  transform: scale(0.95);
  transition: transform 0.1s;
}

.fa-solid, .fa-regular {
  font-size: 1rem;
}
</style>